'use client'

import { ChevronDown, X } from 'lucide-react'
import * as React from 'react'

import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

/**
 * Progressive disclosure primitives for the homepage.
 *
 * The 2026 redesign keeps every section down to an icon, a title and a short
 * summary; the full spec copy lives behind these controls. `MoreLink` is the
 * inline trigger, `MoreDialog` the panel it opens.
 */

type MoreLinkProps = {
  label: string
  onClick: () => void
  className?: string
  /** `subtle` renders muted, `accent` picks up the card's audience colour. */
  tone?: 'accent' | 'subtle'
}

export function MoreLink({ label, onClick, className, tone = 'accent' }: MoreLinkProps) {
  return (
    <button data-component-id="src/components/home/Disclosure.tsx:27:4" data-component-path="src/components/home/Disclosure.tsx" data-component-line="27" data-component-end-line="42" data-component-file="Disclosure.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
      type="button"
      onClick={onClick}
      className={cn(
        'group/more inline-flex items-center gap-1 text-sm font-semibold transition',
        tone === 'accent' ? 'text-gwt-blue hover:text-gwt-blue-dark' : 'text-gwt-muted hover:text-gwt-ink',
        className,
      )}
    >
      {label}
      <ChevronDown data-component-id="src/components/home/Disclosure.tsx:37:6" data-component-path="src/components/home/Disclosure.tsx" data-component-line="37" data-component-end-line="41" data-component-file="Disclosure.tsx" data-component-name="ChevronDown" data-component-content="%7B%22elementName%22%3A%22ChevronDown%22%2C%22className%22%3A%22h-4%20w-4%20-rotate-90%20transition%20group-hover%2Fmore%3Atranslate-x-0.5%22%7D"
        className="h-4 w-4 -rotate-90 transition group-hover/more:translate-x-0.5"
        strokeWidth={2.25}
        aria-hidden
      />
    </button>
  )
}

type MoreDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  closeLabel: string
  eyebrow?: string
  children: React.ReactNode
  footer?: React.ReactNode
  className?: string
}

export function MoreDialog({
  open,
  onOpenChange,
  title,
  closeLabel,
  eyebrow,
  children,
  footer,
  className,
}: MoreDialogProps) {
  return (
    <Dialog data-component-id="src/components/home/Disclosure.tsx:68:4" data-component-path="src/components/home/Disclosure.tsx" data-component-line="68" data-component-end-line="92" data-component-file="Disclosure.tsx" data-component-name="Dialog" data-component-content="%7B%22elementName%22%3A%22Dialog%22%7D" open={open} onOpenChange={onOpenChange}>
      <DialogContent data-component-id="src/components/home/Disclosure.tsx:69:6" data-component-path="src/components/home/Disclosure.tsx" data-component-line="69" data-component-end-line="91" data-component-file="Disclosure.tsx" data-component-name="DialogContent" data-component-content="%7B%22elementName%22%3A%22DialogContent%22%7D" className={cn('max-w-2xl', className)}>
        <DialogHeader data-component-id="src/components/home/Disclosure.tsx:70:8" data-component-path="src/components/home/Disclosure.tsx" data-component-line="70" data-component-end-line="75" data-component-file="Disclosure.tsx" data-component-name="DialogHeader" data-component-content="%7B%22elementName%22%3A%22DialogHeader%22%7D">
          {eyebrow ? (
            <p data-component-id="src/components/home/Disclosure.tsx:72:12" data-component-path="src/components/home/Disclosure.tsx" data-component-line="72" data-component-end-line="72" data-component-file="Disclosure.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22gwt-eyebrow%20text-%5B11px%5D%20font-semibold%20text-gwt-blue%22%7D" className="gwt-eyebrow text-[11px] font-semibold text-gwt-blue">{eyebrow}</p>
          ) : null}
          <DialogTitle data-component-id="src/components/home/Disclosure.tsx:74:10" data-component-path="src/components/home/Disclosure.tsx" data-component-line="74" data-component-end-line="74" data-component-file="Disclosure.tsx" data-component-name="DialogTitle" data-component-content="%7B%22elementName%22%3A%22DialogTitle%22%7D">{title}</DialogTitle>
        </DialogHeader>

        <button data-component-id="src/components/home/Disclosure.tsx:77:8" data-component-path="src/components/home/Disclosure.tsx" data-component-line="77" data-component-end-line="84" data-component-file="Disclosure.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20top-4%20inline-flex%20h-9%20w-9%20items-center%20justify-center%20rounded-lg%20text-gwt-muted%20transition%20hover%3Abg-gwt-surface%20hover%3Atext-gwt-ink%22%7D"
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label={closeLabel}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gwt-muted transition hover:bg-gwt-surface hover:text-gwt-ink"
        >
          <X data-component-id="src/components/home/Disclosure.tsx:83:10" data-component-path="src/components/home/Disclosure.tsx" data-component-line="83" data-component-end-line="83" data-component-file="Disclosure.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%2C%22className%22%3A%22h-4.5%20w-4.5%22%7D" className="h-4.5 w-4.5" aria-hidden />
        </button>

        <DialogBody data-component-id="src/components/home/Disclosure.tsx:86:8" data-component-path="src/components/home/Disclosure.tsx" data-component-line="86" data-component-end-line="86" data-component-file="Disclosure.tsx" data-component-name="DialogBody" data-component-content="%7B%22elementName%22%3A%22DialogBody%22%2C%22className%22%3A%22px-6%20py-5%22%7D" className="px-6 py-5">{children}</DialogBody>

        {footer ? (
          <div data-component-id="src/components/home/Disclosure.tsx:89:10" data-component-path="src/components/home/Disclosure.tsx" data-component-line="89" data-component-end-line="89" data-component-file="Disclosure.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22shrink-0%20border-t%20border-gwt-line%20bg-white%20px-6%20py-4%22%7D" className="shrink-0 border-t border-gwt-line bg-white px-6 py-4">{footer}</div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
